import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Ruler, Plus, ScanLine, Loader2, TrendingDown, TrendingUp, Minus } from "lucide-react";
import { toast } from "sonner";
import MobileLayout from "@/components/MobileLayout";
import { useLanguage } from "@/i18n/LanguageProvider";
import { AgendaBottomSheet } from "@/components/agenda/CreateEventModal";
import { ModuleHistoryList } from "@/components/history/ModuleHistoryList";
import { useBodyMeasurements, type BodyMeasurement } from "@/hooks/useBodyMeasurements";
import { BodyHealthActionModal, BodyHealthPendingActionLauncher } from '@/components/mirror/BodyHealthActionModal';
import { newBodyHealthOperationId, type BodyHealthActionPrefill } from '@/lib/bodyHealthExecutor';
import { comparableBodyMeasurements } from '@/lib/bodyHealthRecordGuards';
import { BODY_HEALTH_CHANGED_EVENT, isBodyHealthChangeFor } from '@/lib/bodyHealthChanges';
import { useBodyReact, reactLanguageFor } from "@/hooks/useBodyReact";
import ReportContentButton from "@/components/report/ReportContentButton";
import { REPORT_SURFACES } from "@/lib/report/surfaces";
import { useReportedContent } from "@/contexts/ReportedContentContext";
import { BODY_SCAN_PROVIDER_ENABLED } from "@/lib/bodyscan/provider";
import { EvolutionChart } from "@/components/charts/EvolutionChart";
import { AnalyticsDashboard } from "@/components/analytics/AnalyticsDashboard";
import { buildDashboard } from "@/lib/analytics/dashboard";
import { BODY_DASHBOARD, extractBodySamples } from "@/lib/analytics/bodyDashboard";
import type { Granularity } from "@/lib/analytics/engine";
import BodyScanFlow from "@/components/fitness/BodyScanFlow";
import { GroupedMeasurements } from "@/components/fitness/GroupedMeasurements";

const num = (value: string): number | null => {
  if (!/^\d+(?:[.,]\d+)?$/.test(value.trim())) return null;
  const parsed = Number(value.trim().replace(',', '.'));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const CHART_SERIES = [
  { field: "weight_kg", color: "hsl(187 72% 50%)", labelKey: "fitness.bodyScan.legendWeight" },
  { field: "waist_cm", color: "hsl(265 60% 65%)", labelKey: "fitness.bodyScan.legendWaist" },
  { field: "body_fat_pct", color: "hsl(30 90% 55%)", labelKey: "fitness.bodyScan.legendBodyFat" },
] as const;

export default function FitnessBodyScan() {
  const body = useBodyMeasurements();
  return <BodyHistoryContent key={body.userId + ":" + body.identityGeneration} body={body} />;
}
function BodyHistoryContent({ body }: { body: ReturnType<typeof useBodyMeasurements> }) {
  const navigate = useNavigate();
  const { t, tf, lang } = useLanguage();
  const { measurements, loading, reload, error, isRefreshing, hasMore, hasData, loadMore, userId, capture } = body;
  const bodyReact = useBodyReact();
  const clearBodyReact = bodyReact.clear;
  const { isReported } = useReportedContent();
  // Identidade da reacao na tela: body_measurements.id da linha que a gerou
  // (o hook devolve so o texto).
  const [reactedRowId, setReactedRowId] = useState<string | null>(null);

  const [quickWeight, setQuickWeight] = useState("");
  const [action, setAction] = useState<BodyHealthActionPrefill | null>(null);
  const saving = !!action;
  const [scanOpen, setScanOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<BodyMeasurement | null>(null);
  const [chartDays, setChartDays] = useState<number>(90);

  const latest = measurements[0] ?? null;
  // Equal timestamps and mixed origins/providers do not establish a change.
  // Keep the newest observed row for each instant in the newest origin's series.
  const comparisonRows = useMemo(() => error ? [] : comparableBodyMeasurements(measurements), [measurements, error]);
  const previous = comparisonRows[1] ?? null;

  // Shared analytics engine — avg weight/body_fat/waist by day/week/month +
  // period-over-period Δ + projection. Complements the EvolutionChart (which
  // shows normalized SHAPE) with observed numeric deltas/projections. No streak (irregular
  // cadence). Valence-neutral trend — no judgment.
  const [bodyGran, setBodyGran] = useState<Granularity>(BODY_DASHBOARD.defaultGranularity);
  const bodyAnalytics = useMemo(
    () => comparisonRows.length ? buildDashboard(extractBodySamples(comparisonRows), BODY_DASHBOARD, bodyGran) : [],
    [comparisonRows, bodyGran],
  );

  const openReview = (kind: 'create' | 'update' | 'delete', row?: BodyMeasurement, fields?: Record<string, unknown>) => {
    if (!userId || !capture()() || row && row.user_id !== userId) return;
    setAction({ resource: 'body', action: kind, operation_id: newBodyHealthOperationId(),
      action_id: newBodyHealthOperationId(), expected_user_id: userId,
      ...(row ? { record_id: row.id } : {}), ...(fields ? { fields: structuredClone(fields) } : {}),
    });
  };
  const handleQuickAdd = () => {
    const weight = num(quickWeight);
    if (weight === null || weight < 20 || weight > 300) { toast.error(t('fitness.bodyScan.invalidWeight')); return; }
    if (saving || !hasData || error || isRefreshing) return;
    openReview('create', undefined, { weight_kg: weight, measured_at: new Date().toISOString() });
  };
  const openCreate = () => openReview('create');
  const openEdit = (row: BodyMeasurement) => openReview('update', row);
  useEffect(() => {
    const changed = (event: Event) => {
      if (!isBodyHealthChangeFor(event, userId, 'body')) return;
      clearBodyReact(); setReactedRowId(null); setDetailRow(null);
    };
    window.addEventListener(BODY_HEALTH_CHANGED_EVENT, changed);
    return () => window.removeEventListener(BODY_HEALTH_CHANGED_EVENT, changed);
  }, [userId, clearBodyReact]);

  // Flag-gated scan flow — full capture→process→result UX lives in
  // BodyScanFlow (fallback capture + vendor quality-check retakes). On save,
  // refresh the page's list and fire the Mirror reaction for the new row.
  const handleScanSaved = (row: BodyMeasurement) => {
    if (!capture()() || row.user_id !== userId) return;
    void reload();
    setReactedRowId(row.id);
    void bodyReact.react(row.id, reactLanguageFor(lang));
  };

  // ── deltas for the hero ────────────────────────────────────────────────
  const heroDeltas = useMemo(() => {
    if (!latest) return [];
    const defs: Array<{ key: string; label: string; unit: string }> = [
      { key: "waist_cm", label: t("fitness.bodyScan.waist"), unit: "cm" },
      { key: "body_fat_pct", label: t("fitness.bodyScan.bodyFat"), unit: "%" },
      { key: "chest_cm", label: t("fitness.bodyScan.chest"), unit: "cm" },
      { key: "hips_cm", label: t("fitness.bodyScan.hips"), unit: "cm" },
    ];
    return defs
      .map((d) => {
        const cur = (latest as Record<string, unknown>)[d.key];
        if (typeof cur !== "number") return null;
        const old = previous ? (previous as Record<string, unknown>)[d.key] : null;
        const delta = typeof old === "number" ? Math.round((cur - old) * 10) / 10 : null;
        return { ...d, value: cur, delta };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null)
      .slice(0, 4);
  }, [latest, previous, t]);

  // ── chart data → shared EvolutionChart series ──────────────────────────
  const chartSeries = useMemo(() => {
    const cutoff = Date.now() - chartDays * 86_400_000;
    const rows = [...comparisonRows]
      .filter((m) => new Date(m.measured_at).getTime() >= cutoff)
      .sort((a, b) => new Date(a.measured_at).getTime() - new Date(b.measured_at).getTime());
    return CHART_SERIES.map((s) => ({
      key: s.field,
      color: s.color,
      label: t(s.labelKey),
      values: rows
        .map((m) => ({ t: new Date(m.measured_at).getTime(), v: (m as Record<string, unknown>)[s.field] }))
        .filter((p): p is { t: number; v: number } => typeof p.v === "number"),
    }));
  }, [comparisonRows, chartDays, t]);

  const hasChart = chartSeries.some((s) => s.values.length >= 2);

  const inputStyle = {
    background: "hsl(222 18% 12%)",
    border: "1px solid hsl(222 18% 22%)",
    color: "white",
  } as const;

  return (
    <MobileLayout className="pb-28 pt-8">
      <button onClick={() => navigate("/fitness")} className="flex items-center gap-1 text-muted-foreground mb-4">
        <ChevronLeft size={18} />
        <span className="text-sm">{t("fitness.title")}</span>
      </button>

      <div className="flex items-center gap-2 mb-5">
        <Ruler size={22} style={{ color: "hsl(187 72% 60%)" }} />
        <h1 className="font-display text-xl font-bold text-white">{t("fitness.bodyScan.title")}</h1>
      </div>

      {error && <div role="alert" className="mb-4 rounded-xl border border-border p-4"><p>{t("bodyHistory.loadError")}</p><button className="min-h-11 underline" disabled={isRefreshing} onClick={() => void reload()}>{t("common.retry")}</button></div>}
      {hasData && <div className="mb-4 space-y-2"><p className="text-sm text-muted-foreground">{tf("bodyHistory.loaded", { count: String(measurements.length) })}</p>{hasMore && <p className="text-sm text-muted-foreground">{t("bodyHistory.partial")}</p>}<button className="min-h-11 underline" disabled={isRefreshing} onClick={() => void reload()}>{t("bodyHistory.refresh")}</button></div>}
      {hasData && latest && <p className="mb-4 text-sm text-muted-foreground">{t("bodyHistory.methodScope")}</p>}
      {hasData && latest?.user_corrected && <p className="mb-4 text-sm text-muted-foreground">{t("bodyHealthAction.userCorrected")}</p>}
      {hasData && latest?.source === "scan" && latest.scan_provider === "mock" && <p className="mb-4 text-sm text-muted-foreground">{t("bodyHealth.scanMockNotice")}</p>}
      {/* ── Latest measurement hero ── */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl p-4 mb-4"
        style={{ background: "hsl(222 18% 10%)", border: "1px solid hsl(187 72% 50% / 0.25)" }}>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="animate-spin text-muted-foreground" size={20} /></div>
        ) : !hasData ? null : !latest ? (
          <p className="text-sm text-muted-foreground py-2">{t("fitness.bodyScan.noData")}</p>
        ) : (
          <>
            <div className="flex items-end gap-2 mb-3">
              {typeof latest.weight_kg === "number" ? (
                <>
                  <span className="font-display text-3xl font-bold text-white">{latest.weight_kg}</span>
                  <span className="text-sm text-muted-foreground mb-1">kg</span>
                  {previous && typeof previous.weight_kg === "number" && (
                    <DeltaBadge delta={Math.round((latest.weight_kg - previous.weight_kg) * 10) / 10} unit="kg" />
                  )}
                </>
              ) : (
                <span className="text-sm text-muted-foreground">{t("fitness.bodyScan.latest")}</span>
              )}
            </div>
            {heroDeltas.length > 0 && (
              <div className="grid grid-cols-2 gap-2">
                {heroDeltas.map((d) => (
                  <div key={d.key} className="rounded-lg px-3 py-2" style={{ background: "hsl(222 18% 13%)" }}>
                    <p className="text-[10px] text-muted-foreground">{d.label}</p>
                    <p className="text-sm font-semibold text-white">
                      {d.value} {d.unit}
                      {d.delta !== null && <DeltaBadge delta={d.delta} unit={d.unit} inline />}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </motion.div>

      {/* ── Mirror reaction (Padrão B) ── */}
      {bodyReact.reaction && !isReported(REPORT_SURFACES.REACTION_BODY, reactedRowId) && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="rounded-xl px-4 py-3 mb-4 text-sm text-white/90 flex items-start justify-between gap-2"
          style={{ background: "hsl(265 60% 40% / 0.15)", border: "1px solid hsl(265 60% 55% / 0.3)" }}>
          <span className="flex-1">{bodyReact.reaction}</span>
          {reactedRowId && (
            <ReportContentButton
              surface={REPORT_SURFACES.REACTION_BODY}
              contentId={reactedRowId}
              snapshot={bodyReact.reaction}
              context={{ module: "body" }}
            />
          )}
        </motion.div>
      )}

      {/* ── Quick add (1-field weight) + full form CTA + flag-gated scan ── */}
      <div className="flex gap-2 mb-5">
        <input
          type="number" inputMode="decimal" step="0.1"
          placeholder={t("fitness.bodyScan.quickWeight")}
          value={quickWeight}
          onChange={(e) => setQuickWeight(e.target.value)}
          className="flex-1 px-3 py-2.5 rounded-xl text-sm"
          style={inputStyle}
        />
        <button onClick={handleQuickAdd} disabled={saving || !hasData || error || isRefreshing}
          className="px-4 rounded-xl font-semibold text-sm"
          style={{ background: "hsl(187 72% 50%)", color: "hsl(222 38% 6%)" }}>
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
        </button>
      </div>
      <div className="flex gap-2 mb-6">
        <button onClick={openCreate} disabled={!hasData || error || isRefreshing}
          className="flex-1 py-2.5 rounded-xl text-sm font-semibold"
          style={{ background: "hsl(222 18% 13%)", border: "1px solid hsl(187 72% 50% / 0.35)", color: "hsl(187 72% 60%)" }}>
          {t("fitness.bodyScan.fullMeasurement")}
        </button>
        {BODY_SCAN_PROVIDER_ENABLED && (
          <button onClick={() => setScanOpen(true)} disabled={!hasData || error || isRefreshing}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-1.5"
            style={{ background: "hsl(265 60% 45% / 0.2)", border: "1px solid hsl(265 60% 55% / 0.4)", color: "hsl(265 60% 72%)" }}>
            <ScanLine size={15} />
            {t("fitness.bodyScan.scanCta")}
          </button>
        )}
      </div>

      {/* ── Evolution chart (house SVG pattern — Progress.tsx energy trend) ── */}
      {hasData && comparisonRows.length > 0 && <div className="rounded-2xl p-4 mb-6" style={{ background: "hsl(222 18% 10%)", border: "1px solid hsl(222 18% 18%)" }}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-white">{t("fitness.bodyScan.chartTitle")}</h2>
          <div className="flex gap-1.5">
            {[30, 90, 365].map((d) => (
              <button key={d} onClick={() => setChartDays(d)}
                className="px-2.5 py-1 rounded-full text-[11px] font-semibold"
                style={chartDays === d
                  ? { background: "hsl(187 72% 50% / 0.18)", color: "hsl(187 72% 60%)", border: "1px solid hsl(187 72% 50% / 0.4)" }
                  : { background: "hsl(222 18% 13%)", color: "hsl(222 10% 60%)", border: "1px solid hsl(222 18% 20%)" }}>
                {t(d === 30 ? "fitness.bodyScan.period30" : d === 90 ? "fitness.bodyScan.period90" : "fitness.bodyScan.period365")}
              </button>
            ))}
          </div>
        </div>
        {!hasChart ? (
          <p className="text-xs text-muted-foreground py-4 text-center">{t("fitness.bodyScan.chartEmpty")}</p>
        ) : (
          <EvolutionChart series={chartSeries} />
        )}
      </div>}

      {/* ── Evolução — shared analytics dashboard (avg weight/body_fat/waist by
          day/week/month + Δ + projection). Valence-neutral, no judgment. ── */}
      {hasData && comparisonRows.length > 0 && <div className="rounded-2xl p-4 mb-6" style={{ background: "hsl(222 18% 10%)", border: "1px solid hsl(222 18% 18%)" }}>
        <h2 className="text-sm font-semibold text-white mb-3">{t("body.analytics.title")}</h2>
        <AnalyticsDashboard
          results={bodyAnalytics}
          granularity={bodyGran}
          onGranularityChange={setBodyGran}
          loading={loading}
          emptyKey="body.analytics.empty"
        />
      </div>}

      {/* ── 📜 History — first consumer of the generic ModuleHistoryList ── */}
      <h2 className="text-sm font-semibold text-white mb-3">{t("fitness.bodyScan.historyTitle")}</h2>
      {hasData && <ModuleHistoryList<BodyMeasurement>
        items={measurements}
        getId={(m) => m.id}
        getDate={(m) => m.measured_at}
        renderItem={(m) => (
          <div className="min-w-0"><button className="block w-full text-left" onClick={() => setDetailRow(m)}>
            <p className="text-sm text-white truncate">
              {[
                typeof m.weight_kg === "number" ? `${m.weight_kg}kg` : null,
                typeof m.waist_cm === "number" ? `${t("fitness.bodyScan.waist")} ${m.waist_cm}` : null,
                typeof m.body_fat_pct === "number" ? `${m.body_fat_pct}%` : null,
                m.source === "scan" ? t("fitness.bodyScan.sourceScan") : null,
              ].filter(Boolean).join(" · ")}
            </p>
            {m.extra_measurements ? (
              <p className="text-[10px]" style={{ color: "hsl(265 60% 70%)" }}>{t("fitness.bodyScan.scan.viewDetails")}</p>
            ) : null}
          </button>{m.user_corrected && <p className="text-xs text-muted-foreground">{t('bodyHealthAction.userCorrected')}</p>}
            <button type="button" className="mt-2 min-h-11 px-3 text-sm text-destructive" disabled={!!action} onClick={() => openReview('delete', m)}>{t('bodyHealthAction.delete')}</button>
          </div>
        )}
        key={userId}
        onEdit={openEdit}
        labels={{
          empty: t("fitness.bodyScan.historyEmpty"),
          deletedToast: t("moduleHistory.deletedToast"),
          undo: t("moduleHistory.undo"),
          period30: t("fitness.bodyScan.period30"),
          period90: t("fitness.bodyScan.period90"),
          periodAll: t("fitness.bodyScan.periodAll"),
        }}
      />}

      {hasMore && <button className="min-h-11 underline" disabled={isRefreshing || error} onClick={() => void loadMore()}>{t(isRefreshing ? "common.loading" : "bodyHistory.loadMore")}</button>}

      {userId && <BodyHealthPendingActionLauncher ownerId={userId} busy={!!action} />}
      <BodyHealthActionModal open={!!action} prefill={action} onClose={() => setAction(null)} onCreated={() => undefined}
        onConfirmed={(response, request) => {
          if (!capture()() || request.resource !== 'body') return;
          setQuickWeight(''); setAction(null); setDetailRow(null);
          if (request.action === 'create' && typeof response.record_id === 'string') {
            setReactedRowId(response.record_id); void bodyReact.react(response.record_id, reactLanguageFor(lang));
          }
        }} />

      {scanOpen && <BodyScanFlow open={scanOpen} onClose={() => setScanOpen(false)} onSaved={handleScanSaved} />}

      {/* ── History detail — full measurement payload, same grouped pattern ── */}
      <AgendaBottomSheet
        open={detailRow !== null}
        onClose={() => setDetailRow(null)}
        title={t("fitness.bodyScan.scan.detailTitle")}
        footer={<div />}
      >
        {detailRow && (
          <div className="pb-2">
            {detailRow.user_corrected && <p className="mb-3 text-sm text-muted-foreground">{t("bodyHealthAction.userCorrected")}</p>}
            {detailRow.extra_measurements ? (
              <GroupedMeasurements raw={detailRow.extra_measurements} />
            ) : (
              <p className="text-sm text-muted-foreground py-4 text-center">{t("fitness.bodyScan.scan.noDetails")}</p>
            )}
          </div>
        )}
      </AgendaBottomSheet>

    </MobileLayout>
  );
}

// Signed delta chip: negative = cyan (usually the goal direction for cm/%),
// positive = neutral gray — the Mirror does the interpreting, not the UI.
function DeltaBadge({ delta, unit, inline }: { delta: number; unit: string; inline?: boolean }) {
  if (delta === 0) {
    return <span className={inline ? "ml-1.5 text-[10px] text-muted-foreground" : "mb-1.5 ml-1 text-xs text-muted-foreground"}><Minus size={10} className="inline" /></span>;
  }
  const down = delta < 0;
  const color = down ? "hsl(187 72% 60%)" : "hsl(222 10% 65%)";
  const Icon = down ? TrendingDown : TrendingUp;
  return (
    <span className={inline ? "ml-1.5 text-[10px] font-semibold" : "mb-1.5 ml-1 text-xs font-semibold"} style={{ color }}>
      <Icon size={inline ? 10 : 12} className="inline mr-0.5" />
      {Math.abs(delta)}{unit}
    </span>
  );
}
